import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
/* MotionConfig — framer-motion animuje přes JavaScript, takže na CSS pravidlo
 * `prefers-reduced-motion` nereaguje. `reducedMotion="user"` ho na systémové
 * nastavení napojí: kdo má animace vypnuté, dostane rovnou koncový stav.
 * Musí obalit CELOU aplikaci, jinak ho část stromu mine. */
import { MotionConfig } from "framer-motion";
import { useEffect, useState, lazy, Suspense } from "react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { AppErrorBoundary, LoadingGate } from "@/components/AppErrorBoundary";
import { ProfileProvider } from "@/contexts/ProfileContext";
import { SeriesDataProvider } from "@/contexts/SeriesDataContext";
import { LanguageProvider, registerTranslations, useLanguage } from "@/contexts/LanguageContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { Toaster } from "sonner";
import LeftSidebar, { W_COLLAPSED, W_EXPANDED } from "@/components/LeftSidebar";
import GlobalTabBar from "@/components/GlobalTabBar";
import GlobalSearch from "@/components/GlobalSearch";
import BottomNav from "@/components/BottomNav";
import { Menu } from "lucide-react";
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const SeriesDetailPage = lazy(() => import("@/pages/SeriesDetailPage"));
const CalculatorsPage = lazy(() => import("@/pages/CalculatorsPage"));
const ToolsPage = lazy(() => import("@/pages/ToolsPage"));
const ByznysPlanPage = lazy(() => import("@/pages/ByznysPlanPage")); // Můj byznys plán poradce (Nástroje → Vzdělávání, 25. 10. 2026)
const SkupinaKalkulacekPage = lazy(() => import("@/pages/SkupinaKalkulacekPage")); // skupiny kalkulaček „v jednom“ (/kalkulacky/:skupina)
const SvobodaPage = lazy(() => import("@/pages/SvobodaPage"));
const HypotekaRozhodnutiPage = lazy(() => import("@/pages/HypotekaRozhodnutiPage"));
const DiarPageV3 = lazy(() => import("@/pages/DiarPageV3"));
const KlientiPage = lazy(() => import("@/pages/KlientiPage"));
const ObchodyPage = lazy(() => import("@/pages/ObchodyPage"));
const DealDetailPage = lazy(() => import("@/pages/DealDetailPage"));
const RealityPipelinePage = lazy(() => import("@/pages/RealityPipelinePage"));
const ZakazkaNaObchod = lazy(() => import("@/pages/ZakazkaNaObchod"));
const RealityZajemciPage = lazy(() => import("@/pages/RealityZajemciPage"));
const RezervacniSmlouvaPage = lazy(() => import("@/pages/RezervacniSmlouvaPage"));
const ProdatNeboNechatPage = lazy(() => import("@/pages/ProdatNeboNechatPage"));
const ProhlidkaPrihlaskaPage = lazy(() => import("@/pages/ProhlidkaPrihlaskaPage"));
const NemovitostWebPage = lazy(() => import("@/pages/NemovitostWebPage"));
const MajitelStavPage = lazy(() => import("@/pages/MajitelStavPage"));
const HypotecniPipelinePage = lazy(() => import("@/pages/HypotecniPipelinePage"));
const HistorickaKalkulackaPage = lazy(() => import("@/pages/HistorickaKalkulackaPage"));
const FixacePage = lazy(() => import("@/pages/FixacePage"));
const FinMatematikaPage = lazy(() => import("@/pages/FinMatematikaPage"));
const InvesticniStrategiePage = lazy(() => import("@/pages/InvesticniStrategiePage"));
const InvesticniStudioPage = lazy(() => import("@/pages/InvesticniStudioPage"));
const FinancniMapaPage = lazy(() => import("@/pages/FinancniMapaPage"));
const AnalyzaV2Page = lazy(() => import("@/pages/AnalyzaV2Page"));
const DesignLabAnalyza2 = lazy(() => import("@/pages/DesignLabAnalyza2")); // interní design lab (mimo produkční nav)
const DesignLabGrafPoklesu = lazy(() => import("@/pages/DesignLabGrafPoklesu")); // graf poklesu příjmu — náhled před zapojením do kalkulačky
const DesignLabBeplanOrange = lazy(() => import("@/pages/DesignLabBeplanOrange")); // náhled motivu „beplan“ (brand oranžová) — NEPŘEBARVUJE appku, tokeny žijí v .bo-scope
const DesignLabMilionovyPoradce = lazy(() => import("@/pages/DesignLabMilionovyPoradce")); // náhled vizuálu pro samostatný produkt milionovyporadce.cz — tokeny žijí v .mp-scope
const VzorkovnaPage = lazy(() => import("@/pages/design-lab/VzorkovnaPage")); // VZORKOVNA — písmo, tokeny, ikony místo emoji, stavy, 6 motivů (interní, mimo nav)
const MozekPage = lazy(() => import("@/pages/design-lab/MozekPage")); // MOZEK BEPLANU — futuristický prototyp, vlastní scope .mozek-scope (interní, mimo nav)
const NahledStyly = lazy(() => import("@/pages/NahledStyly")); // srovnání tří vizuálních směrů (tokeny v .st-a/.st-b/.st-c)
const NovyCrmApp = lazy(() => import("@/novy-crm/NovyCrmApp")); // NOVÝ CRM — prototyp „bokem“ (DESIGN-novy.md), vlastní rozvržení, jen čtení
const Analyza2BetaPage = lazy(() => import("@/pages/Analyza2BetaPage")); // Analýza 2.0 funkční beta (chráněná, mimo produkční nav)
const NavrhReseniPage = lazy(() => import("@/pages/NavrhReseniPage"));
/* Staré řešení plánu je zase výchozí (majitel 17. 10. 2026: „nesmaž hlavně to starý řešení“);
 * nový Návrh v 9 krocích zůstává na /navrh-novy. */
const NavrhReseniStaryPage = lazy(() => import("@/pages/NavrhReseniStaryPage"));
/* Návrh (beta) – kasička s chytrým návrhem + její tisk (25. 10. 2026, PLAN-KASICKA-BETA.md). */
const NavrhBetaPage = lazy(() => import("@/pages/NavrhBetaPage"));
const NavrhBetaTiskPage = lazy(() => import("@/pages/NavrhBetaTiskPage"));
const NavrhBetaPrezentacePage = lazy(() => import("@/pages/NavrhBetaPrezentacePage"));
/* Shrnutí — rozdělovník plánu (tři počítadla + řádek na cíl).
 * Vlastní routa, ne záložka v Návrhu: celá oblast plánu je routa na výstup
 * (navrh / prezentace / plan-strana / plan-vystup) a rozdělovník se má dát
 * poslat odkazem i vytisknout sám za sebe. Dnešní karty v Návrhu zůstávají. */
const ShrnutiPlanuPage = lazy(() => import("@/pages/ShrnutiPlanuPage"));
const PlanPrezentacePage = lazy(() => import("@/pages/PlanPrezentacePage"));
const OnePagePlanPage = lazy(() => import("@/pages/OnePagePlanPage"));
const AnalyzaTiskPage = lazy(() => import("@/pages/AnalyzaTiskPage"));
const PlanVystupPage = lazy(() => import("@/pages/PlanVystupPage"));
const ZivotniPojisteniKalkulackaPage = lazy(() => import("@/pages/ZivotniPojisteniKalkulackaPage"));
/* /penze = nová penzijní kalkulačka (DPS + DIP, historie, fondy, renta) — majitel 4. 10. 2026 „dej to místo ty staré“. */
const PenzijniKalkulackaPage = lazy(() => import("@/pages/PenzijniKalkulackaPage"));
/* Proc pojistka (IZP) - vysvetlovaci nastroj vedle zivotni kalkulacky. */
const IzpPage = lazy(() => import("@/pages/IzpPage"));
const AnalyzaVietnamRodinaPage = lazy(() => import("@/pages/AnalyzaVietnamRodinaPage"));
const RentaPorovnavacPage = lazy(() => import("@/pages/RentaPorovnavacPage"));
const PrezenterPage = lazy(() => import("@/pages/PrezenterPage")); // prezentér první schůzky (A1) — fullscreen, mimo AppLayout
const ServisRadarPage = lazy(() => import("@/pages/ServisRadarPage"));
const CrmKomunikacePage = lazy(() => import("@/pages/CrmKomunikacePage"));
const KalkulackaVizualPage = lazy(() => import("@/pages/KalkulackaVizualPage"));
const SmlouvaDetailPage = lazy(() => import("@/pages/SmlouvaDetailPage"));
const ManagerPage = lazy(() => import("@/pages/ManagerPage"));
const NaborPipelinePage = lazy(() => import("@/pages/NaborPipelinePage"));
const CnbPoradciPage = lazy(() => import("@/pages/CnbPoradciPage"));
const ClientMapPage = lazy(() => import("@/pages/ClientMapPage"));
const RadarPrilezitostiPage = lazy(() => import("@/pages/RadarPrilezitostiPage"));
const ProvizniKalkulackaPage = lazy(() => import("@/pages/ProvizniKalkulackaPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
/* Co je nového / Čo je nové — přehled změn pro poradce (data/novinky.js), odkaz s tečkou v levé liště. */
const NovinkyPage = lazy(() => import("@/pages/NovinkyPage"));
const VizitkaPage = lazy(() => import("@/pages/VizitkaPage"));
const InformaceKandidatiPage = lazy(() => import("@/pages/InformaceKandidatiPage"));
const WebPage = lazy(() => import("@/pages/WebPage"));
const WebSettingsPage = lazy(() => import("@/pages/WebSettingsPage"));
const VizitkaSettingsPage = lazy(() => import("@/pages/VizitkaSettingsPage"));
const CRMLayout = lazy(() => import("@/pages/crm/CRMLayout"));
const CRMDashboardPage = lazy(() => import("@/pages/crm/CRMDashboardPage"));
const CRMLeadsPage = lazy(() => import("@/pages/crm/CRMLeadsPage"));
const CRMCallsPage = lazy(() => import("@/pages/crm/CRMCallsPage"));
const CRMClientsPage = lazy(() => import("@/pages/crm/CRMClientsPage"));
const CRMIKPage = lazy(() => import("@/pages/crm/CRMIKPage"));
const CRMGoalsPage = lazy(() => import("@/pages/crm/CRMGoalsPage"));
const CRMTeamPage = lazy(() => import("@/pages/crm/CRMTeamPage"));
const CRMVyhlaseniPage = lazy(() => import("@/pages/crm/CRMVyhlaseniPage"));
// Týmové akce — porady a teambuildingy (manažer). Vlastní routa pod /crm.
const CRMAkcePage = lazy(() => import("@/pages/crm/CRMAkcePage"));
/* Diář týmu — manažerský náhled do diáře poradce podle JEHO souhlasu
   (přepínač má poradce v Nastavení). Obrazovka žije v components/manazer/diar,
   protože je to celý ucelený modul, ne jen stránka. */
const CRMDiarTymuPage = lazy(() => import("@/components/manazer/diar/DiarTymuObrazovka"));
const CRMReferralsPage = lazy(() => import("@/pages/crm/CRMReferralsPage"));
const CRMScriptsPage = lazy(() => import("@/pages/crm/CRMScriptsPage"));
const AcademyLayout = lazy(() => import("@/pages/academy/AcademyLayout"));
const AcademyAcquisitionPage = lazy(() => import("@/pages/academy/AcademyAcquisitionPage"));
const ProduktovaAkademiePage = lazy(() => import("@/pages/academy/ProduktovaAkademiePage"));
const StartKitPage = lazy(() => import("@/pages/academy/StartKitPage"));
const SedmidenniAdaptacePage = lazy(() => import("@/pages/academy/SedmidenniAdaptacePage"));
const ManazerAdaptacePage = lazy(() => import("@/pages/academy/ManazerAdaptacePage"));
const MetodikaAkademiePage = lazy(() => import("@/pages/academy/MetodikaAkademiePage"));
const ZkouskaPage = lazy(() => import("@/pages/academy/ZkouskaPage"));
/* Hloubkové materiály vietnamské stopy. Data (vi/pojisteni.js,
   curriculumUveryVN.js) mají stejný tvar modulu jako akademie, ale ID nejsou
   v backend/academy_progress.MODULE_ORDER — postup se u nich schválně
   NEUKLÁDÁ a stránka to říká nahlas. Vstup je z /akademie/produkty → Tiếng Việt. */
const ViHloubkaPage = lazy(() => import("@/pages/academy/ViHloubkaPage"));
const BookingSettingsPage = lazy(() => import("@/pages/booking/BookingSettingsPage"));
const PublicBookingPage = lazy(() => import("@/pages/booking/PublicBookingPage"));
const BookingSpravaPage = lazy(() => import("@/pages/booking/BookingSpravaPage")); // přeplánovat/zrušit schůzku odkazem z e-mailu
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const ReferralPage = lazy(() => import("@/pages/ReferralPage"));
/* Pozvánka do týmu (14. 10. 2026) — veřejná, funguje i bez účtu. */
const PozvankaPage = lazy(() => import("@/pages/PozvankaPage"));
const ModelacePage = lazy(() => import("@/pages/ModelacePage"));
const HypotekaDotaznikPage = lazy(() => import("@/pages/HypotekaDotaznikPage"));
const KlientskyFormularPage = lazy(() => import("@/pages/KlientskyFormularPage"));
const LeadGenPage = lazy(() => import("@/pages/LeadGenPage"));
const LeadGen3DPage = lazy(() => import("@/pages/LeadGen3DPage"));
const KvizFunnelPage = lazy(() => import("@/pages/KvizFunnelPage"));
const SilaCasuMagnetPage = lazy(() => import("@/pages/SilaCasuMagnetPage"));
/* Akvizice (1. 11. 2026): lead magnet a trvalá stránka Doporučte mě — veřejné, bez přihlášení. */
const MagnetPage = lazy(() => import("@/pages/MagnetPage"));
const DoporuceniPage = lazy(() => import("@/pages/DoporuceniPage"));
const FunnelModulPage = lazy(() => import("@/pages/FunnelModulPage"));
const TrychtyrePage = lazy(() => import("@/pages/TrychtyrePage"));
const FunnelKnihovnaPage = lazy(() => import("@/pages/FunnelKnihovnaPage")); // knihovna 53 hotových funnelů + skládač zadání (skrytá dlaždice, adminOnly)
const TrychtyrBuilderPage = lazy(() => import("@/pages/TrychtyrBuilderPage")); // Trychtýř od A do Z — průvodce 7 kroky (Konverzky), uložení per poradce
const AIKnowledgePage = lazy(() => import("@/pages/AIKnowledgePage"));
const VoiceNotesPage = lazy(() => import("@/pages/VoiceNotesPage"));
const AdminBroadcastEmailPage = lazy(() => import("@/pages/admin/AdminBroadcastEmailPage"));
const DataHygienePage = lazy(() => import("@/pages/admin/DataHygienePage"));
const TrzniDataPage = lazy(() => import("@/pages/admin/TrzniDataPage"));
const MesicnikEditorPage = lazy(() => import("@/pages/admin/MesicnikEditorPage"));
const MesicnikRozeslatPage = lazy(() => import("@/pages/mesicnik/MesicnikRozeslatPage"));
const MesicnikVerejnyPage = lazy(() => import("@/pages/mesicnik/MesicnikVerejnyPage"));
const MesicnikOdhlaseniPage = lazy(() => import("@/pages/mesicnik/MesicnikOdhlaseniPage"));
const MesicnikOdberPage = lazy(() => import("@/pages/mesicnik/MesicnikOdberPage"));
const PostaPage = lazy(() => import("@/pages/PostaPage"));
const VoiceFieldModePage = lazy(() => import("@/pages/VoiceFieldModePage"));
import FirstLoginWizard from "@/components/FirstLoginWizard";
import VoiceRecorderWidget from "@/components/VoiceRecorderWidget"; // eslint-disable-line no-unused-vars
const PortfolioDoctorPage = lazy(() => import("@/pages/PortfolioDoctorPage"));
// Portfolio doktor (nákladová diagnostika, bez AI) — jiný nástroj než
// admin/portfolio-doctor výše; počítá se celý na frontendu z lib/portfolio.
const PortfolioDoktorPage = lazy(() => import("@/pages/PortfolioDoktorPage"));
const SrovnavacFonduPage = lazy(() => import("@/pages/SrovnavacFonduPage"));
const NemovitostPronajemPage = lazy(() => import("@/pages/NemovitostPronajemPage"));
/* Nástroje ke schůzce — import vypnutý spolu s routou (25. 8. 2026).
 * Nechávám ho tu zakomentovaný, ať je při návratu vidět, kam patří:
 * const NastrojeSchuzkaPage = lazy(() => import("@/pages/NastrojeSchuzkaPage")); */
const SsoLandingPage = lazy(() => import("@/pages/SsoLandingPage"));
import MobileLayout from "@/components/mobile/MobileLayout";
const MobileHomePage = lazy(() => import("@/pages/mobile/MobileHomePage"));
import useIsMobile from "@/hooks/useIsMobile";
import AIAssistantWidget from "@/components/AIAssistantWidget";
import InstallPrompt from "@/components/InstallPrompt";
import ClientFolderHost from "@/components/ClientFolderHost";
// StartHereGuide dočasně vypnuto (viz níže) — import zakomentován, ať build nepadá na unused.
// import StartHereGuide from "@/components/onboarding/StartHereGuide";
import CookieBanner from "@/components/CookieBanner";
import cs from "@/data/translations/cs";
import sk from "@/data/translations/sk";
import uk from "@/data/translations/uk";
import vi from "@/data/translations/vi";
import en from "@/data/translations/en";
import { TL } from "@/lib/prekladyUI";
import { zapnuto } from "@/lib/funkce"; // přepínač funkcí (rezervační smlouva, videa) — viz lib/funkce.js
/* Obrazovky týmu pouští TymBrana (role NEBO potvrzený tým, lib/mujTym), ne role. */
import TymBrana from "@/components/manazer/TymBrana";
import { cestaPozvanky, prectiPozvanku } from "@/lib/pozvankaOdkaz";

registerTranslations("cs", cs);
registerTranslations("sk", sk);
registerTranslations("uk", uk);
registerTranslations("vi", vi);
registerTranslations("en", en);

function ProtectedRoute({ children, adminOnly = false, roles = null }) {
  const { user, loading } = useAuth();
  const { lang } = useLanguage();
  const location = useLocation();
  if (loading) return <LoadingGate message={TL("Načítám tvůj účet…", lang)} />;
  if (!user) {
    // Preserve the full destination (pathname + query + hash) so we can
    // return the user there after they finish authenticating. Marketing
    // links like /?utm_source=share, /klienti?focus=xyz, /sso?token=abc
    // all keep their query parameters intact through the login round-trip.
    const dest = `${location.pathname}${location.search || ""}${location.hash || ""}`;
    return <Navigate to="/login" state={{ from: dest }} replace />;
  }
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

/* /poptavky/:id → /obchody/:id.
 * `:id` u poptavky VZDY bylo id obchodu (poptavka je zaznam v `deals`
 * s product_type="koupe_nemovitosti"), takze to je ciste prejmenovani adresy.
 * Presmerovani, ne smazani routy: odkaz uz je v poznamkach i v zalozkach. */
function PoptavkaNaObchod() {
  const { id } = useParams();
  return <Navigate to={`/obchody/${id}`} replace />;
}

/* RootRedirect — Landing page is now CRM-first (the main daily tool for advisors).
 * Preserves query string from the original URL (e.g. /?utm_source=share) so
 * marketing/tracking parameters keep flowing to the destination route.
 * Video series (vzdělávací akademie) moved to /vzdelavani in the top navigation.
 */
function RootRedirect() {
  const location = useLocation();
  /* Návrat z SSO na pozvánku do týmu: SSO vždy končí na „/“. Stránka pozvánky
     před odchodem uložila token (ověřený regexem, platí 2 h); cestu skládá
     cestaPozvanky, z úložiště se nikdy nečte celá adresa. */
  const pozvanka = cestaPozvanky(prectiPozvanku());
  if (pozvanka) return <Navigate to={pozvanka} replace />;
  // Preserve search + hash so marketing/tracking links survive the redirect.
  const target = `/crm${location.search || ""}${location.hash || ""}`;
  return <Navigate to={target} replace />;
}

/* Post-auth redirect — when an already-authed user lands on /login (e.g. via
 * a shared link with ?utm_source=share + state.from), return them to the
 * URL they originally wanted. Falls back to root → CRM.
 */
function PostAuthRedirect() {
  const location = useLocation();
  const destFromState = location.state?.from;
  const dest = typeof destFromState === "string" && destFromState.startsWith("/") && !destFromState.startsWith("//")
    ? destFromState
    : `/${location.search || ""}${location.hash || ""}`;
  return <Navigate to={dest} replace />;
}

/* Mobile tab shims — reuse existing pages inside the mobile shell for now,
 * wrapped with padding so they don't collide with the bottom tab bar.
 */
function MobileCrmWrapper({ children }) {
  return <div className="px-4">{children}</div>;
}
function CRMLeadsListMobile() {
  return (
    <MobileCrmWrapper>
      <CRMLeadsPage />
    </MobileCrmWrapper>
  );
}
function MobileVoiceRedirect() {
  // The mobile voice/REC tab is for every advisor → the field voice mode,
  // NOT the admin-only voice-notes page (which bounces a regular advisor away).
  // Hlasový režim volá placenou AI — bez přepínače aiTlacitka zpět na /m.
  return <Navigate to={zapnuto("aiTlacitka") ? "/voice-field" : "/m"} replace />;
}
function MobileCalculatorsRedirect() {
  return <Navigate to="/calculators" replace />;
}
function MobileProfilePage() {
  return <Navigate to="/settings" replace />;
}

function AppLayout({ children }) {
  const { lang } = useLanguage();
  const [sidebarLocked, setSidebarLocked] = useState(() => localStorage.getItem("sidebar-locked") === "1");
  // Mobil (< lg / 1024px): sidebar je off-canvas drawer → obsah BEZ offsetu, hamburger nahoře.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 1024
  );
  useEffect(() => {
    const onToggle = (e) => setSidebarLocked(!!e.detail?.locked);
    window.addEventListener("sidebar-toggle", onToggle);
    return () => window.removeEventListener("sidebar-toggle", onToggle);
  }, []);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 1023px)");
    const onChange = () => setIsMobile(window.innerWidth < 1024);
    onChange();
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    window.addEventListener("resize", onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
      window.removeEventListener("resize", onChange);
    };
  }, []);
  // Na mobilu žádný levý offset (drawer se překrývá přes obsah, ne odsazuje).
  const sidebarWidth = isMobile ? 0 : (sidebarLocked ? W_EXPANDED : W_COLLAPSED);
  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <LeftSidebar />
      {/* Hamburger — jen mobil. Otevře off-canvas drawer (LeftSidebar poslouchá event). */}
      {isMobile && (
        <button
          onClick={() => window.dispatchEvent(new CustomEvent("sidebar-drawer-toggle"))}
          className="fixed z-[85] flex items-center justify-center rounded-xl"
          style={{
            top: "calc(env(safe-area-inset-top) + 8px)",
            left: 10,
            width: 40,
            height: 40,
            background: "var(--surface)",
            border: "1px solid var(--line)",
            color: "var(--ink)",
            boxShadow: "var(--shadow-card, 0 2px 10px rgba(38,34,26,0.08))",
          }}
          data-testid="mobile-hamburger"
          aria-label={TL("Otevřít menu", lang)}
          title="Menu"
        >
          <Menu size={20} />
        </button>
      )}
      <div
        className="flex flex-col min-h-screen"
        style={{ paddingLeft: sidebarWidth, transition: "padding-left 0.22s cubic-bezier(0.32, 0.72, 0, 1)" }}
      >
        <GlobalTabBar />
        <main className="flex-1 pb-20 lg:pb-8">
          {children}
        </main>
      </div>
      <GlobalSearch />
      <BottomNav />
      {/* StartHereGuide („Začni tady") dočasně vypnuto na přání — chovalo se divně (mizelo při změně záložky). Vrátíme po předělání. */}
      {/* <StartHereGuide /> */}
      <FirstLoginWizard />
      {/* VoiceRecorderWidget removed — replaced by Smart Onboarding (in /klienti) and Voice Field Mode */}
      {/* AIAssistantWidget dočasně skryt — vpravo dole je teď univerzální „+" (rychlé přidání klient/obchod/nábor) */}
      {/* <AIAssistantWidget /> */}
      {/* <InstallPrompt /> disabled — user feedback: PWA install banner not wanted */}
      <ClientFolderHost />
    </div>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();
  const { lang } = useLanguage();
  if (loading) return <LoadingGate message={TL("Načítám…", lang)} />;
  return (
    <Suspense fallback={<LoadingGate message={TL("Načítám stránku…", lang)} slowAfter={6000} />}>
    <Routes>
      <Route path="/login" element={user ? <PostAuthRedirect /> : <LoginPage />} />
      <Route path="/v/:slug" element={<VizitkaPage />} />
      <Route path="/informace-kandidati/:slug" element={<InformaceKandidatiPage />} />
      <Route path="/w/:slug" element={<WebPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/d/:token" element={<ReferralPage />} />
      {/* Pozvánka do týmu — veřejná, mimo AppLayout (nezaloží záložku). Přijmout
          smí jen zvaný; stránka sama nic nezpřístupní. */}
      <Route path="/pozvanka/:token" element={<PozvankaPage />} />
      <Route path="/m/:token" element={<ModelacePage />} />
      {/* Měsíčník pro klienty — veřejná webová verze a odhlášení (token na příjemce) */}
      <Route path="/mesicnik/odhlasit/:token" element={<MesicnikOdhlaseniPage />} />
      <Route path="/mesicnik/odber/:slug" element={<MesicnikOdberPage />} />
      <Route path="/mesicnik/:cislo/:token" element={<MesicnikVerejnyPage />} />
      {/* Hypoteční dotazník pro klienta. Veřejná routa BEZ přihlášení —
          jediným klíčem je token v adrese, id klienta se sem nikdy nedostane. */}
      <Route path="/hd/:token" element={<HypotekaDotaznikPage />} />
      {/* Obecný klientský formulář (zdravotní dotazník, podklad k pojistné
          smlouvě…). Veřejná routa BEZ přihlášení — jediným klíčem je token
          v adrese, id klienta se sem nikdy nedostane. `?lang=` volí poradce
          při vytvoření odkazu, klient si jazyk může přepnout na stránce. */}
      <Route path="/f/:token" element={<KlientskyFormularPage />} />
      {/* Přihláška na prohlídku. Veřejná routa BEZ přihlášení — jediným klíčem
          je token v adrese. Souhlasy se sbírají tady, protože zájemce stejně
          něco vyplňuje; makléře to nestojí ani minutu. */}
      <Route path="/pr/:token" element={<ProhlidkaPrihlaskaPage />} />
      {/* Web nemovitosti pro kupující. Slug je čitelný schválně — makléř ho
          diktuje do telefonu. Veřejné BEZ přihlášení; server vydá jen to, co
          makléř vědomě zveřejnil (snímek v `reality_weby`), nikdy zakázku. */}
      <Route path="/n/:slug" element={<NemovitostWebPage />} />
      {/* Stav prodeje pro MAJITELE. Tady je klíčem token, ne slug: čte se živý
          stav zakázky (fáze, počty, prohlídky) a to soused vědět nemá. */}
      <Route path="/z/:token" element={<MajitelStavPage />} />
      <Route path="/l/:topic/:slug" element={<LeadGenPage />} />
      {/* /d/:token patří ReferralPage (odkaz pro jednoho klienta), proto trvalá
          stránka poradce „Doporučte mě“ žije na /doporucit/:slug. */}
      <Route path="/doporucit/:slug" element={<DoporuceniPage />} />
      <Route path="/magnet/:slug/:magnet" element={<MagnetPage />} />
      <Route path="/kviz/:slug" element={<KvizFunnelPage />} />
      <Route path="/sila-casu/:slug" element={<SilaCasuMagnetPage />} />
      <Route path="/t/:id/:slug" element={<FunnelModulPage />} />
      {/* 3D LeadGen — Executive Dark funnel with rotating Three.js building.
          Public, no auth. Slug is optional and used for future personalization. */}
      <Route path="/3d" element={<LeadGen3DPage />} />
      <Route path="/3d/:slug" element={<LeadGen3DPage />} />
      <Route path="/" element={<ProtectedRoute><RootRedirect /></ProtectedRoute>} />
      {/* Vzdělávací videa jsou VYPNUTÁ přepínačem (lib/funkce.js, Pan Čung 2. 10. 2026):
          videa se teprve natáčejí. Obě adresy kreslí TUTÉŽ stránku videosérií, proto
          obě míří do Akademie (moduly, infografiky, zkouška zůstávají). Přesměrování
          BEZ AppLayout, jinak by lišta stihla založit záložku. Routy nemazat. */}
      <Route path="/desktop" element={zapnuto("vzdelavaciVidea") ? <ProtectedRoute><AppLayout><DashboardPage /></AppLayout></ProtectedRoute> : <ProtectedRoute><Navigate to="/akademie/produkty" replace /></ProtectedRoute>} />
      <Route path="/vzdelavani" element={zapnuto("vzdelavaciVidea") ? <ProtectedRoute><AppLayout><DashboardPage /></AppLayout></ProtectedRoute> : <ProtectedRoute><Navigate to="/akademie/produkty" replace /></ProtectedRoute>} />
      <Route path="/m" element={<ProtectedRoute><MobileLayout /></ProtectedRoute>}>
        <Route index element={<MobileHomePage />} />
        <Route path="crm" element={<CRMLeadsListMobile />} />
        <Route path="voice" element={<MobileVoiceRedirect />} />
        <Route path="calc" element={<MobileCalculatorsRedirect />} />
        <Route path="profile" element={<MobileProfilePage />} />
      </Route>
      {/* Přehrávač série (zápis postupu) — za stejným přepínačem jako výš. */}
      <Route path="/series/:id" element={zapnuto("vzdelavaciVidea") ? <ProtectedRoute><AppLayout><SeriesDetailPage /></AppLayout></ProtectedRoute> : <ProtectedRoute><Navigate to="/akademie/produkty" replace /></ProtectedRoute>} />
      <Route path="/calculators" element={<ProtectedRoute><AppLayout><CalculatorsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/srovnavac-fondu" element={<ProtectedRoute><AppLayout><SrovnavacFonduPage /></AppLayout></ProtectedRoute>} />
      <Route path="/portfolio-doktor" element={<ProtectedRoute><AppLayout><PortfolioDoktorPage /></AppLayout></ProtectedRoute>} />
      <Route path="/mesicnik" element={<ProtectedRoute><AppLayout><MesicnikRozeslatPage /></AppLayout></ProtectedRoute>} />
      <Route path="/nemovitost-pronajem" element={<ProtectedRoute><AppLayout><NemovitostPronajemPage /></AppLayout></ProtectedRoute>} />
        {/* Nástroje ke schůzce (zlato / konsolidace / podpojištění / doplacení).
            ROUTY ODPOJENY 25. 8. 2026 na přání majitele — kalkulačky mu nedávaly
            smysl a budeme je stavět znovu společně. Komponenty, engine i testy
            zůstávají v repu; vrátí se sem dva řádky <Route path="/kalkulacka…">
            a čtyři dlaždice v ToolsPage.js, jakmile se dohodne podoba. */}
      <Route path="/klienti" element={<ProtectedRoute><AppLayout><KlientiPage /></AppLayout></ProtectedRoute>} />
      <Route path="/obchody" element={<ProtectedRoute><AppLayout><ObchodyPage /></AppLayout></ProtectedRoute>} />
      <Route path="/obchody/:id" element={<ProtectedRoute><AppLayout><DealDetailPage /></AppLayout></ProtectedRoute>} />
      {/* Realitni zakazky. Vec (nemovitost) je vlastni kolekce, POSTUP ale jede
          po existujici pipeline obchodu (product_type="prodej_nemovitosti"). */}
      {/* Realitni pipeline. Stejny vzor jako /hypoteky: radek na nemovitost,
          tecky faze, teplota podle toho, jak dlouho to stoji. */}
      <Route path="/reality" element={<ProtectedRoute><AppLayout><RealityPipelinePage /></AppLayout></ProtectedRoute>} />
      <Route path="/crm/reality" element={<ProtectedRoute><AppLayout><RealityPipelinePage /></AppLayout></ProtectedRoute>} />
      {/* Seznam zakazek uz neni: /reality ukazuje OBE strany (prodej i nakup)
          v tomtez prehledu, ktery pouziva hypotecni pipeline. Adresa se ale
          nerusi, jen presmerovava — je v zalozkach i v poznamkach makleru. */}
      <Route path="/zakazky" element={<ProtectedRoute><Navigate to="/reality" replace /></ProtectedRoute>} />
      {/* Karta zakazky uz neni: realitni zakazka JE obchodni pripad a otevira
          se na /obchody/:id. Tahle routa jen dohleda obchod a presmeruje. */}
      <Route path="/zakazky/:id" element={<ProtectedRoute><AppLayout><ZakazkaNaObchod /></AppLayout></ProtectedRoute>} />
      <Route path="/zakazky/:zakazkaId/zajemci" element={<ProtectedRoute><AppLayout><RealityZajemciPage /></AppLayout></ProtectedRoute>} />
      {/* Druha strana realitniho obchodu: poptavka na NAKUP. Jeji `:id` VZDY
          bylo id obchodu (kolekce `deals`, product_type="koupe_nemovitosti"),
          takze presmerovani je primocare — zadne dohledavani. */}
      <Route path="/poptavky/:id" element={<ProtectedRoute><PoptavkaNaObchod /></ProtectedRoute>} />
      {/* Rezervacni smlouva. Vlastni routa, ne overlay: makler ji vyplnuje na
          druhem monitoru vedle karty zakazky a musi na ni jit poslat odkaz. */}
      {/* Propocet prodat-nebo-nechat pro schuzku s MAJITELEM nemovitosti.
          Vlastni routa ze stejneho duvodu jako rezervacni smlouva: makler ji
          otaci ke klientovi a musi ji otevrit i bez zalozene zakazky. */}
      <Route path="/prodat-nebo-nechat" element={<ProtectedRoute><AppLayout><ProdatNeboNechatPage /></AppLayout></ProtectedRoute>} />
      {/* Rezervacni smlouva je VYPNUTA prepinacem (lib/funkce.js, Pan Cung 2. 10. 2026).
          Routy zustavaji (odkazy v zalozkach a poznamkach), jen presmeruji na Obchody.
          Presmerovani BEZ AppLayout: jinak by GlobalTabBar stihl zalozit zalozku. */}
      <Route path="/rezervacni-smlouva" element={zapnuto("rezervacniSmlouva") ? <ProtectedRoute><AppLayout><RezervacniSmlouvaPage /></AppLayout></ProtectedRoute> : <ProtectedRoute><Navigate to="/obchody" replace /></ProtectedRoute>} />
      <Route path="/rezervacni-smlouva/:id" element={zapnuto("rezervacniSmlouva") ? <ProtectedRoute><AppLayout><RezervacniSmlouvaPage /></AppLayout></ProtectedRoute> : <ProtectedRoute><Navigate to="/obchody" replace /></ProtectedRoute>} />
      <Route path="/crm/hypoteky" element={<ProtectedRoute><AppLayout><HypotecniPipelinePage /></AppLayout></ProtectedRoute>} />
      <Route path="/hypoteky" element={<ProtectedRoute><AppLayout><HypotecniPipelinePage /></AppLayout></ProtectedRoute>} />
      <Route path="/nabor" element={<ProtectedRoute><AppLayout><NaborPipelinePage /></AppLayout></ProtectedRoute>} />
      <Route path="/nabor/cnb" element={<ProtectedRoute><AppLayout><CnbPoradciPage /></AppLayout></ProtectedRoute>} />
      <Route path="/mapa-klientu" element={<ProtectedRoute><AppLayout><ClientMapPage /></AppLayout></ProtectedRoute>} />
      <Route path="/crm/radar-prilezitosti" element={<ProtectedRoute><AppLayout><RadarPrilezitostiPage /></AppLayout></ProtectedRoute>} />
      <Route path="/provize" element={<ProtectedRoute><AppLayout><ProvizniKalkulackaPage /></AppLayout></ProtectedRoute>} />
      <Route path="/smlouvy/:id" element={<ProtectedRoute><AppLayout><SmlouvaDetailPage /></AppLayout></ProtectedRoute>} />
      <Route path="/diar" element={<ProtectedRoute><AppLayout><DiarPageV3 /></AppLayout></ProtectedRoute>} />
      <Route path="/posta" element={<ProtectedRoute><AppLayout><PostaPage /></AppLayout></ProtectedRoute>} />
      <Route path="/voice-field" element={zapnuto("aiTlacitka") ? <ProtectedRoute><VoiceFieldModePage /></ProtectedRoute> : <ProtectedRoute><Navigate to="/" replace /></ProtectedRoute>} />
      <Route path="/manazer" element={<ProtectedRoute><TymBrana><AppLayout><ManagerPage /></AppLayout></TymBrana></ProtectedRoute>} />
      <Route path="/tools" element={<ProtectedRoute><AppLayout><ToolsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/trychtyre" element={<ProtectedRoute><AppLayout><TrychtyrePage /></AppLayout></ProtectedRoute>} />
      <Route path="/nastroje" element={<ProtectedRoute><AppLayout><ToolsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/kalkulacky/:skupina/:zalozka?" element={<ProtectedRoute><AppLayout><SkupinaKalkulacekPage /></AppLayout></ProtectedRoute>} />
      {/* „Zatím jen pro mě" (majitel). Brána je na DVOU místech, stejně jako
        * u /admin/portfolio-doctor: dlaždice v ToolsPage má `adminOnly`, aby ji
        * poradce nenašel, a routa má `adminOnly`, aby se na ni nedostal ani přes
        * odkaz. Až se to má otevřít všem, smaž `adminOnly` na obou místech. */}
      <Route path="/nastroje/funnel-knihovna" element={<ProtectedRoute adminOnly><AppLayout><FunnelKnihovnaPage /></AppLayout></ProtectedRoute>} />
      <Route path="/nastroje/trychtyr" element={<ProtectedRoute><AppLayout><TrychtyrBuilderPage /></AppLayout></ProtectedRoute>} />
      <Route path="/nastroje/byznys-plan" element={<ProtectedRoute><AppLayout><ByznysPlanPage /></AppLayout></ProtectedRoute>} />
      <Route path="/svoboda" element={<ProtectedRoute><AppLayout><SvobodaPage /></AppLayout></ProtectedRoute>} />
      <Route path="/hypoteka-rozhodnuti" element={<ProtectedRoute><AppLayout><HypotekaRozhodnutiPage /></AppLayout></ProtectedRoute>} />
      <Route path="/historicka-kalkulacka" element={<ProtectedRoute><HistorickaKalkulackaPage /></ProtectedRoute>} />
      <Route path="/fixace" element={<ProtectedRoute><FixacePage /></ProtectedRoute>} />
      <Route path="/hypotecni-fixace" element={<ProtectedRoute><FixacePage /></ProtectedRoute>} />
      <Route path="/fin-matematika" element={<ProtectedRoute><AppLayout><FinMatematikaPage /></AppLayout></ProtectedRoute>} />
      <Route path="/investicni-strategie" element={<ProtectedRoute><AppLayout><InvesticniStrategiePage /></AppLayout></ProtectedRoute>} />
      <Route path="/investicni-studio" element={<ProtectedRoute><AppLayout><InvesticniStudioPage /></AppLayout></ProtectedRoute>} />
      <Route path="/zivotni-pojisteni" element={<ProtectedRoute><ZivotniPojisteniKalkulackaPage /></ProtectedRoute>} />
      <Route path="/penze" element={<ProtectedRoute><PenzijniKalkulackaPage /></ProtectedRoute>} />
      {/* Bez AppLayoutu jako ostatni kalkulacky u stolu: notebook se otaci ke klientovi. */}
      <Route path="/analyza-vietnam" element={<ProtectedRoute><IzpPage /></ProtectedRoute>} />
      <Route path="/analyza-vietnam/rodina" element={<ProtectedRoute><AnalyzaVietnamRodinaPage /></ProtectedRoute>} />
      {/* Stará adresa z doby, kdy se nástroj jmenoval „Proč pojistka (IŽP)". Majitel 29. 9. 2026:
          „IŽP nedávej název, je to analýza pro Vietnamce." Záložky a odkazy dál fungují. */}
      <Route path="/izp" element={<Navigate to="/analyza-vietnam" replace />} />
      {/* Porovnávač tří způsobů čerpání renty — klientská obrazovka, proto bez AppLayoutu (stejně jako ostatní kalkulačky). */}
      <Route path="/renta-porovnavac" element={<ProtectedRoute><RentaPorovnavacPage /></ProtectedRoute>} />
      <Route path="/prezenter" element={<ProtectedRoute><PrezenterPage /></ProtectedRoute>} />
      <Route path="/akademie/produkty" element={<ProtectedRoute><AppLayout><ProduktovaAkademiePage /></AppLayout></ProtectedRoute>} />
      <Route path="/akademie/start" element={<ProtectedRoute><AppLayout><StartKitPage /></AppLayout></ProtectedRoute>} />
      <Route path="/akademie/adaptace" element={<ProtectedRoute><AppLayout><SedmidenniAdaptacePage /></AppLayout></ProtectedRoute>} />
      <Route path="/akademie/manazer" element={<ProtectedRoute><AppLayout><ManazerAdaptacePage /></AppLayout></ProtectedRoute>} />
      <Route path="/akademie/metodika" element={<ProtectedRoute><AppLayout><MetodikaAkademiePage /></AppLayout></ProtectedRoute>} />
      <Route path="/akademie/zkouska" element={<ProtectedRoute><AppLayout><ZkouskaPage /></AppLayout></ProtectedRoute>} />
      <Route path="/akademie/vi/:tema" element={<ProtectedRoute><AppLayout><ViHloubkaPage /></AppLayout></ProtectedRoute>} />
      <Route path="/crm/servis-radar" element={<ProtectedRoute><AppLayout><ServisRadarPage /></AppLayout></ProtectedRoute>} />
      <Route path="/crm/komunikace" element={<ProtectedRoute><AppLayout><CrmKomunikacePage /></AppLayout></ProtectedRoute>} />
      <Route path="/crm/clients/:cid/mapa" element={<ProtectedRoute><FinancniMapaPage /></ProtectedRoute>} />
      <Route path="/crm/clients/:cid/analyza" element={<ProtectedRoute><AnalyzaV2Page /></ProtectedRoute>} />
      {/* Analýza 2.0 funkční beta — chráněná, MIMO hlavní navigaci (LeftSidebar/BottomNav bez záznamu). */}
      <Route path="/crm/clients/:clientId/analyza-2-beta" element={<ProtectedRoute><Analyza2BetaPage /></ProtectedRoute>} />
      <Route path="/design-lab/analyza-2" element={<ProtectedRoute><DesignLabAnalyza2 /></ProtectedRoute>} />
      <Route path="/design-lab/graf-poklesu" element={<ProtectedRoute><DesignLabGrafPoklesu /></ProtectedRoute>} />
      <Route path="/design-lab/beplan-orange" element={<ProtectedRoute><DesignLabBeplanOrange /></ProtectedRoute>} />
      <Route path="/design-lab/vzorkovna" element={<ProtectedRoute><VzorkovnaPage /></ProtectedRoute>} />
      <Route path="/design-lab/mozek" element={<ProtectedRoute><MozekPage /></ProtectedRoute>} />
      {/* Veřejný náhled — majitel si ho má otevřít i z mobilu bez přihlášení. Jen statická ukázka, žádná data. */}
      <Route path="/nahled/milionovy-poradce" element={<DesignLabMilionovyPoradce />} />
      <Route path="/nahled/styly" element={<NahledStyly />} />
      {/* NOVÝ CRM — prototyp „bokem“ (30. 9. 2026, src/novy-crm/DESIGN-novy.md). Vlastní
          celoobrazovkové rozvržení BEZ AppLayout (jinak by nad ním byla lišta hlavní appky),
          jen ochrana přihlášením. Data jen ke čtení, podstránky řeší NovyCrmApp. */}
      <Route path="/novy-crm/*" element={<ProtectedRoute><NovyCrmApp /></ProtectedRoute>} />
      {/* 29. 10. 2026 (majitel: „tohle můžeš dát už pryč… a nasadit rovnou to“): Návrh = beta.
          Starý Návrh zůstává skrytě na /navrh-stary (výstupy a tisk z něj dál čtou stejná data). */}
      <Route path="/crm/clients/:cid/navrh" element={<ProtectedRoute><NavrhBetaPage /></ProtectedRoute>} />
      <Route path="/crm/clients/:cid/navrh-stary" element={<ProtectedRoute><NavrhReseniStaryPage /></ProtectedRoute>} />
      <Route path="/crm/clients/:cid/navrh-novy" element={<ProtectedRoute><NavrhReseniPage /></ProtectedRoute>} />
      <Route path="/crm/clients/:cid/navrh-beta" element={<ProtectedRoute><NavrhBetaPage /></ProtectedRoute>} />
      <Route path="/crm/clients/:cid/navrh-beta/tisk" element={<ProtectedRoute><NavrhBetaTiskPage /></ProtectedRoute>} />
      <Route path="/crm/clients/:cid/navrh-beta/prezentace" element={<ProtectedRoute><NavrhBetaPrezentacePage /></ProtectedRoute>} />
      <Route path="/crm/clients/:cid/shrnuti" element={<ProtectedRoute><ShrnutiPlanuPage /></ProtectedRoute>} />
      <Route path="/crm/clients/:cid/prezentace" element={<ProtectedRoute><PlanPrezentacePage /></ProtectedRoute>} />
      <Route path="/crm/clients/:cid/plan-strana" element={<ProtectedRoute><OnePagePlanPage /></ProtectedRoute>} />
      <Route path="/crm/clients/:cid/analyza-tisk" element={<ProtectedRoute><AnalyzaTiskPage /></ProtectedRoute>} />
      <Route path="/crm/clients/:cid/plan-vystup" element={<ProtectedRoute><PlanVystupPage /></ProtectedRoute>} />
      <Route path="/historical" element={<ProtectedRoute><HistorickaKalkulackaPage /></ProtectedRoute>} />
      <Route path="/kalkulacka-vizual" element={<ProtectedRoute><AppLayout><KalkulackaVizualPage /></AppLayout></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><AppLayout><SettingsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/nastaveni" element={<ProtectedRoute><AppLayout><SettingsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/nastaveni/web" element={<ProtectedRoute><AppLayout><WebSettingsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/nastaveni/vizitka" element={<ProtectedRoute><AppLayout><VizitkaSettingsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/novinky" element={<ProtectedRoute><AppLayout><NovinkyPage /></AppLayout></ProtectedRoute>} />
      <Route path="/crm" element={<ProtectedRoute><AppLayout><CRMLayout /></AppLayout></ProtectedRoute>}>
        <Route index element={<CRMDashboardPage />} />
        <Route path="leads" element={<CRMLeadsPage />} />
        <Route path="calls" element={<CRMCallsPage />} />
        <Route path="clients" element={<CRMClientsPage />} />
        {/* IK konzultace a Tým — manažerské obrazovky, stejná brána jako lišta
            (LeftSidebar) a /manazer: TymBrana = role admin/manager NEBO aspoň jeden
            potvrzený člen týmu (14. 10. 2026 — roli manager v appce nikdo nepřidělí,
            tým vzniká přijetím pozvánky). Ručně napsaná adresa bez týmu → „/“. */}
        <Route path="ik" element={<TymBrana><CRMIKPage /></TymBrana>} />
        <Route path="goals" element={<CRMGoalsPage />} />
        <Route path="team" element={<TymBrana><CRMTeamPage /></TymBrana>} />
        {/* Vyhlášení výsledků — čistě manažerská obrazovka, proto role gate
            navíc (data poradců stejně hlídá backend přes team_relationships). */}
        <Route path="vyhlaseni" element={<TymBrana><CRMVyhlaseniPage /></TymBrana>} />
        <Route path="akce" element={<CRMAkcePage />} />
        <Route path="diar-tymu" element={<CRMDiarTymuPage />} />
        <Route path="referrals" element={<Navigate to="/crm/leadgen" replace />} />
        {/* QA-E4 — /crm/diar přesměruj na hlavní /diar (jednotná cesta) */}
        <Route path="diar" element={<Navigate to="/diar" replace />} />
        <Route path="posta" element={<Navigate to="/posta" replace />} />
        <Route path="obchody" element={<Navigate to="/obchody" replace />} />
        <Route path="hypoteky" element={<Navigate to="/hypoteky" replace />} />
      </Route>
      <Route path="/crm/leadgen" element={<ProtectedRoute><AppLayout><CRMReferralsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/akademie" element={<ProtectedRoute><AppLayout><AcademyLayout /></AppLayout></ProtectedRoute>}>
        <Route index element={<CRMScriptsPage />} />
        <Route path="skripty" element={<CRMScriptsPage />} />
        <Route path="akvizice" element={<AcademyAcquisitionPage />} />
      </Route>
      <Route path="/rezervace" element={<ProtectedRoute><AppLayout><BookingSettingsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/book/:slug" element={<PublicBookingPage />} />
      <Route path="/book/sprava/:token" element={<BookingSpravaPage />} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><AppLayout><AdminPage /></AppLayout></ProtectedRoute>} />
      <Route path="/admin/ai-knowledge" element={<ProtectedRoute adminOnly><AppLayout><AIKnowledgePage /></AppLayout></ProtectedRoute>} />
      <Route path="/admin/voice-notes" element={<ProtectedRoute adminOnly><AppLayout><VoiceNotesPage /></AppLayout></ProtectedRoute>} />
      <Route path="/admin/email-broadcast" element={<ProtectedRoute adminOnly><AppLayout><AdminBroadcastEmailPage /></AppLayout></ProtectedRoute>} />
      <Route path="/admin/portfolio-doctor" element={<ProtectedRoute adminOnly><AppLayout><PortfolioDoctorPage /></AppLayout></ProtectedRoute>} />
      <Route path="/admin/data-hygiene" element={<ProtectedRoute adminOnly><AppLayout><DataHygienePage /></AppLayout></ProtectedRoute>} />
      <Route path="/admin/trzni-data" element={<ProtectedRoute adminOnly><AppLayout><TrzniDataPage /></AppLayout></ProtectedRoute>} />
      <Route path="/admin/mesicnik" element={<ProtectedRoute adminOnly><AppLayout><MesicnikEditorPage /></AppLayout></ProtectedRoute>} />
      <Route path="/sso" element={<SsoLandingPage />}/>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
  );
}

function WwwRedirect({ children }) {
  // Auto-redirect www.<domain> → <domain> (drop www)
  if (typeof window !== "undefined" && window.location.hostname.startsWith("www.")) {
    const newHost = window.location.hostname.replace(/^www\./, "");
    const newUrl = `${window.location.protocol}//${newHost}${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.location.replace(newUrl);
    return null;
  }
  return children;
}

export default function App() {
  // Disable Google Translate auto-translate — we have our own LanguageProvider
  // with hand-curated translations. Auto-translate produced offensive Ukrainian
  // strings (e.g. "Domluvit schůzku" → vulgar UA term).
  useEffect(() => {
    document.documentElement.setAttribute("translate", "no");
    document.documentElement.classList.add("notranslate");
  }, []);

  return (
    <AppErrorBoundary>
    <WwwRedirect>
      <MotionConfig reducedMotion="user">
      <BrowserRouter>
        <LanguageProvider>
          <ThemeProvider>
            <AuthProvider>
              <ProfileProvider>
                <SeriesDataProvider>
                  <AppRoutes />
                  <CookieBanner />
                  {/* Decentní toasty: krátké trvání, jemný nájezd, neblokují — Pan Čung: „ať to skáče decentně, nemusím čekat" */}
                  <Toaster
                    position="top-center"
                    theme="dark"
                    richColors
                    duration={1800}
                    gap={8}
                    visibleToasts={2}
                    offset={16}
                    toastOptions={{
                      style: { borderRadius: 14, fontSize: "13px", padding: "10px 14px" },
                    }}
                  />
                </SeriesDataProvider>
              </ProfileProvider>
            </AuthProvider>
          </ThemeProvider>
        </LanguageProvider>
      </BrowserRouter>
      </MotionConfig>
    </WwwRedirect>
    </AppErrorBoundary>
  );
}
